
/* eslint-disable vue/no-side-effects-in-computed-properties */
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import type { IMediaContent } from '../types/lightBox';
import { lsTest } from '../helpers/localstorage';
import { EventBus } from '../eventbus';

export default defineComponent({
	name: 'Lightbox',
	data() {
		const maskToShow = '' as 'mask-left' | 'mask-right' | 'mask-both' | '';
		const animationName = 'swipe-left' as 'swipe-right' | 'swipe-left';

		return {
			localStorageAvailable: lsTest(),
			userHasAcceptedExternalCookies: false,
			animationName,
			currentPosition: 0,
			visible: false,
			previousBodyOverflowSetting: '',
			touchHandling: {
				posX: 0,
				posY: 0
			},
			maskToShow
		};
	},
	computed: {
		currentItem(): IMediaContent {
			return this.media[this.currentPosition];
		},
		visiblePreviewItems(): IMediaContent[] {
			// there are only 5 preview items or less -> return unchanged array & dont apply mask
			if (this.media.length <= 5) {
				this.maskToShow = '';
				return this.media;
			}
			// there are more than 5 preview items and the currentposition hasnt crossed the third item -> return first 5 & apply mask to outer right item
			if (this.currentPosition <= 1) {
				this.maskToShow = 'mask-right';
				return this.media.slice(0, 5);
			}
			// currentposition in given interval  -> return previous 2 and next 2 items & apply mask to both outer items
			if (this.currentPosition >= 2 && this.media.length - this.currentPosition >= 3) {
				if (this.currentPosition >= 3 && this.media.length - this.currentPosition >= 4) {
					this.maskToShow = 'mask-both';
				} else if (this.currentPosition >= 3) {
					this.maskToShow = 'mask-left';
				} else if (this.media.length - this.currentPosition >= 4) {
					this.maskToShow = 'mask-right';
				}
				return this.media.slice(this.currentPosition - 2, this.currentPosition + 3);
			}
			// currentposition approaching last 3 items -> return last 5 & apply mask to outer left item
			this.maskToShow = 'mask-left';
			return this.media.slice(this.media.length - 5);
		}
	},
	mounted() {
		window.addEventListener('keyup', event => this.keyUpChecker(event));

		const acceptedCookie = this.localStorageAvailable && localStorage.getItem('accept-cookies');
		if (acceptedCookie && acceptedCookie.startsWith('[')) {
			const parsedCookie = JSON.parse(acceptedCookie);
			this.userHasAcceptedExternalCookies = !!parsedCookie.includes('externalYoutube');
		}

		EventBus().$on('accepted-tracking', parsedCookie => {
			this.userHasAcceptedExternalCookies = !!parsedCookie.includes('externalYoutube');
		});
	},
	beforeDestroy() {
		// make sure scrolling is enabled again if user manages to navigate inside lightbox
		this.removeScrollLock();
		window.removeEventListener('keyup', event => this.keyUpChecker(event));
		EventBus().$off('accepted-tracking');
	},
	methods: {
		keyUpChecker(event) {
			switch (event.key) {
				case 'Left': // IE/Edge specific value
				case 'ArrowLeft':
					this.moveToPreviousItem();
					break;
				case 'Right': // IE/Edge specific value
				case 'ArrowRight':
					this.moveToNextItem();
					break;
				case 'Escape':
					this.closeLightbox();
					break;
				default:
				// do nothing
			}
		},
		setCurrentPosition(url) {
			this.currentPosition = this.media.findIndex(item => item.url === url);
		},
		moveToNextItem() {
			this.animationName = 'swipe-left';
			if (this.currentPosition === this.media.length - 1) {
				this.currentPosition = 0;
			} else {
				this.currentPosition += 1;
			}
			this.$emit('requestNextSilentBoxItem');
		},
		moveToPreviousItem() {
			this.animationName = 'swipe-right';
			if (this.currentPosition === 0) {
				this.currentPosition = this.media.length - 1;
			} else {
				this.currentPosition -= 1;
			}
			this.$emit('requestPreviousSilentBoxItem');
		},
		touchStart(event: TouchEvent) {
			const { clientX: x, clientY: y } = event.touches[0];
			this.touchHandling.posX = x;
			this.touchHandling.posY = y;
		},
		touchMove($event: TouchEvent) {
			const { clientX: x, clientY: y } = $event.touches[0];
			const { posX, posY } = this.touchHandling;
			// prevent any y-axis scrolling
			if (posY) {
				$event.preventDefault();
			}
			if (posX === 0 || posY === 0) {
				return;
			}
			const xDiff = posX - x;
			const yDiff = posY - y;
			if (Math.abs(xDiff) > Math.abs(yDiff)) {
				if (xDiff > 0) {
					// left
					this.moveToNextItem();
				} else {
					// right
					this.moveToPreviousItem();
				}
			}
			this.touchHandling.posX = 0;
			this.touchHandling.posY = 0;
		},
		enableScrollLock() {
			// save previous overflow style and set it to hidden to prevent any scrolling interaction
			this.previousBodyOverflowSetting = document.body.style.overflow;
			document.body.style.overflow = 'hidden';
		},
		removeScrollLock() {
			// restore original state and reset settings
			if (document.body.style.overflow) {
				document.body.style.overflow = this.previousBodyOverflowSetting;
				this.previousBodyOverflowSetting = '';
			}
		},
		openLightbox(item: IMediaContent) {
			if (item?.url) {
				this.setCurrentPosition(item.url);
			}

			this.enableScrollLock();
			this.visible = true;
			this.$emit('opened');
		},
		closeLightbox() {
			if (this.visible) {
				this.removeScrollLock();
				this.visible = false;
				this.$emit('closed', this.currentPosition);
			}
		},
		openCookieBanner() {
			this.$cookieBanner.toggle(true, { stage: 'config' });
		}
	},
	props: {
		media: { type: Array as PropType<IMediaContent[]>, required: true },
		showCaption: { type: Boolean, default: true }
	}
});
